import React, { useState, useEffect } from 'react'
import { Box, Tabs, Tab } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { RoleManager } from '../../../APIManager'
import RoleTable from './RoleTable'

const TAB_STORAGE_KEY = 'rolePermissionsTabValue'

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index, ...other }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`role-tabpanel-${index}`}
    aria-labelledby={`role-tab-${index}`}
    {...other}
  >
    {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
  </div>
)

const TabbedRoleManagement: React.FC = () => {
  // Load tab value from localStorage on mount
  const [tabValue, setTabValue] = useState(() => {
    const savedTab = localStorage.getItem(TAB_STORAGE_KEY)
    return savedTab ? parseInt(savedTab, 10) : 0
  })

  // Save tab value to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem(TAB_STORAGE_KEY, tabValue.toString())
  }, [tabValue])

  const { data: rolesSummary, isLoading: isRolesLoading } = useQuery({
    queryKey: ['rolesSummary'],
    queryFn: async () => {
      const response = await RoleManager.getRolesSummary()
      return response.data
    }
  })

  const handleTabChange = (_: React.SyntheticEvent, newValue: number): void => {
    setTabValue(newValue)
  }

  // Find role IDs for Account Owner and System Manager
  const accountOwnerRole = rolesSummary?.find((role: any) => role.roleName === 'Account Owner')
  const systemManagerRole = rolesSummary?.find((role: any) => role.roleName === 'System Manager')

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="role management tabs">
          <Tab label="Account Owner" id="role-tab-0" aria-controls="role-tabpanel-0" />
          <Tab label="System Manager" id="role-tab-1" aria-controls="role-tabpanel-1" />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        <RoleTable
          roleName="Account Owner"
          roleKey="accountOwners"
          roleId={accountOwnerRole?.roleId}
          isLoading={isRolesLoading}
        />
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <RoleTable
          roleName="System Manager"
          roleKey="systemManagers"
          roleId={systemManagerRole?.roleId}
          isLoading={isRolesLoading}
        />
      </TabPanel>
    </Box>
  )
}

export default TabbedRoleManagement
